'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MangaPhotoStep } from '@/components/manga/MangaPhotoStep';
import { MangaCharactersStep } from '@/components/manga/MangaCharactersStep';
import { charactersFromFaces, type MangaCharacterDraft, type MangaPhoto } from '@/components/manga/types';
import { bookRequestHeaders } from '@/components/manga/request-headers';
import { trackCreate } from '@/utils/create-flow-analytics';
import { unfilledCentralRoles } from '@/lib/picture-book/central-roles';
import { initialLanguageFromParam } from '@/lib/languages/initial-language';
import type { IdeaOccasion } from '@/lib/catalogue/idea-cards';
import { pictureBookResumeTarget } from '@/lib/books/product-type';
import { clearStoredPreviewBook, readStoredPreviewBook, safeSessionStorage, storePreviewBook } from '@/lib/comics/preview-session';
import { PictureBookDetailsStep, emptyDetailsValue, type DetailsValue } from './PictureBookDetailsStep';
import { applyCoverSeed, loadCoverSeed, seedFailed } from './cover-seed';
import { PictureBookPreviewStep } from './PictureBookPreviewStep';
import { PictureBookStoryStep } from './PictureBookStoryStep';
import {
  PICTURE_BOOK_FLOW_STEPS,
  buildPictureBookPreviewPayload,
  clearStoredEntriesForBook,
  detailsChangeFields,
  ideaSourceOf,
  parentDetails,
  resolveStoredBook,
  STORED_BOOK_RETRY_MESSAGE,
  type EnumerableStorage,
  type FetchedJson,
  pictureBookPreviewKey,
  routeDetailsChange,
  seedParentStory,
  seedParentStyle,
  withBookToken,
  type BookDetails,
  type PictureBookFlowStep,
} from './flow-state';

const STEP_LABELS: Record<PictureBookFlowStep, string> = {
  photo: 'Photo',
  people: 'People',
  'your-book': 'Your book',
  preview: 'Preview',
  story: 'Story',
};

interface PictureBookFlowProps {
  /** `?book=`: resume a book this flow made (the stamp decides, never the flag). */
  initialBookId?: string;
  /** `?language=`, already validated by the page. */
  initialLanguage?: string;
  /** ?style= on /create-book: seeds the Your book style as the parent's own choice when the flow offers it. */
  initialStyle?: string;
  /** ?story= on /create-book: pre-selects that story template on Your book (ignored with ?book= or ?fromCover=). */
  initialStory?: string;
  /** `?fromCover=` (free-cover / MCP continue): seed the photo, title and idea from the visitor's cover. */
  initialFromCover?: { slug: string; t: string | null };
  /** Catalogue display names by template id for the central-role notice (the page passes them; server data). */
  storyNames?: Readonly<Record<string, string>>;
  /** ?idea= on /create-book (validated by the page): that idea card fills Your book (ignored with ?story, ?book, ?fromCover). */
  initialIdea?: string;
  /** ?occasion= on /create-book: that occasion's ideas come first. */
  initialOccasion?: IdeaOccasion;
}

/** The preview book for the current inputs: its key (people, photo, language, page count) and the style/title/idea it has. */
interface PreviewBook {
  key: string;
  bookId: string;
  details: BookDetails;
}

const detailsOf = (d: DetailsValue): BookDetails => ({ style: d.style, title: d.title, idea: d.idea });

/**
 * The parent-facing picture-book flow on the comic engine: Photo -> People -> Your book -> Preview -> Story.
 * Holds the photo, the people, the book details and the preview book. Dark behind PICTURE_BOOK_COMIC_ENGINE (the
 * /create-book page decides; see lib/create-book/flow-choice.ts); a `?book=` resume works whatever the flag says.
 */
export function PictureBookFlow({
  initialBookId,
  initialLanguage,
  initialStyle,
  initialStory,
  initialFromCover,
  storyNames,
  initialIdea,
  initialOccasion,
}: PictureBookFlowProps) {
  const router = useRouter();
  // Only the ?book= the page was opened with is resumed: the flow's own router.replace to ?book= is not a resume
  const resumeFrom = useRef(initialBookId || null);
  const [resuming, setResuming] = useState(!!resumeFrom.current);
  const [resumeError, setResumeError] = useState<string | null>(null);
  // Free-cover continue: the seed is loaded once, before the first step is shown. `t` (a legacy owner key) is
  // exchanged for the owner cookie by the continue route, never sent on from here.
  const coverFrom = useRef(initialBookId ? null : initialFromCover ?? null);
  const [seeding, setSeeding] = useState(!!coverFrom.current);
  const [coverPhoto, setCoverPhoto] = useState<File | null>(null);
  const [coverSlug, setCoverSlug] = useState<string | null>(null);
  // The seed route's answer: the cover holds a stored photo consent (R5, no new tick); false shows the box
  const [coverConsented, setCoverConsented] = useState(false);
  const [coverNotice, setCoverNotice] = useState<string | null>(null);
  const [fromFreeCover, setFromFreeCover] = useState(false);
  const firstView = useRef(true);
  const [accessToken, setAccessToken] = useState('');
  const [step, setStep] = useState<PictureBookFlowStep>('photo');
  const [photo, setPhoto] = useState<MangaPhoto | null>(null);
  const [characters, setCharacters] = useState<MangaCharacterDraft[]>([]);
  const storySeed = initialBookId || initialFromCover ? undefined : initialStory;
  // ?story wins over ?idea; a resume and a cover continue ignore both
  const ideaSeed = initialBookId || initialFromCover || initialStory ? undefined : initialIdea;
  const [details, setDetails] = useState<DetailsValue>(() => seedParentStory(seedParentStyle(emptyDetailsValue(initialLanguage || 'en'), initialStyle), storySeed));
  // The ?story= template that was seeded, reported once on mount (read-and-clear: strict mode runs effects twice)
  const preselected = useRef<string | undefined>(details.templateId);
  const [previewBook, setPreviewBook] = useState<PreviewBook | null>(null);
  // A style/title/idea change from Your book, sent to the change route by the preview step
  const [pendingChange, setPendingChange] = useState<Partial<BookDetails> | null>(null);
  // Owner decision FLAG 1: the parent edited the story, so style, title, idea and reading level are fixed
  const [locked, setLocked] = useState(false);
  // Your book -> Next is checking a stored preview book
  const [checking, setChecking] = useState(false);
  const [nextError, setNextError] = useState<string | null>(null);
  const nextBusy = useRef(false);
  const nextRun = useRef(0);

  useEffect(() => {
    const template = preselected.current;
    preselected.current = undefined;
    if (template) trackCreate('picture-book', 'template_preselected', { template });
  }, []);

  // Same as the old wizard: a partner link's code is kept for checkout; ?language= seeds the book language.
  useEffect(() => {
    let params: URLSearchParams | null = null;
    try {
      params = new URLSearchParams(window.location.search);
      const partnerCode = params.get('partner');
      if (partnerCode) localStorage.setItem('partnerCode', partnerCode);
      if (!initialLanguage) {
        const lang = initialLanguageFromParam(params.get('language'));
        if (lang) setDetails((d) => ({ ...d, language: lang }));
      }
    } catch {
      /* storage blocked: checkout simply has no partner code */
    }
    const id = resumeFrom.current;
    if (id) void resume(id, params?.get('bt') || params?.get('t') || '');
    const cover = coverFrom.current;
    coverFrom.current = null; // once (React strict mode runs effects twice)
    if (cover) {
      if (cover.t) {
        // Never keep an owner key in the address bar or analytics: exchange it once for the owner cookie
        window.location.replace(`/api/free-cover/continue?slug=${encodeURIComponent(cover.slug)}&t=${encodeURIComponent(cover.t)}`);
        return;
      }
      void loadCoverSeed(cover.slug, null).then((r) => {
        if (!seedFailed(r)) {
          setCoverPhoto(r.seed.photoFile);
          setCoverSlug(cover.slug);
          setCoverConsented(r.seed.photoConsented);
          setDetails((d) => applyCoverSeed(d, r.seed));
          setFromFreeCover(true);
        } else if (r.reason === 'expired') {
          setCoverNotice(r.message ?? null);
          setFromFreeCover(true);
        }
        // denied / error: a plain flow, no message
        setSeeding(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** ?book=: open a stamped unpaid book at its step; anything else opens at /book/<id>/preview (no loop: see that page). */
  const resume = async (id: string, token: string) => {
    setAccessToken(token);
    try {
      const headers = await bookRequestHeaders();
      const res = await fetch(withBookToken(`/api/books/${encodeURIComponent(id)}`, token), { headers, cache: 'no-store' });
      if (!res.ok) {
        setResumeError(
          res.status === 403 || res.status === 404
            ? 'This book link is no longer valid in this browser. Please use the link from your email, or start a new book.'
            : "We couldn't open your book. Please try again in a moment.",
        );
        return;
      }
      const book = (await res.json())?.book ?? null;
      const target = pictureBookResumeTarget(book);
      if (target === 'flow-preview' || target === 'flow-story') {
        setPreviewBook({ key: '', bookId: id, details: parentDetails(book) });
        setStep(target === 'flow-story' ? 'story' : 'preview');
        setResuming(false);
        return;
      }
      router.replace(withBookToken(`/book/${encodeURIComponent(id)}/preview`, token));
    } catch {
      setResumeError("We couldn't open your book. Please try again in a moment.");
    }
  };

  useEffect(() => {
    // A ?book= resume never shows the photo step: no step view until it lands on its step.
    if (resuming || seeding) return;
    // The first view of a visitor who came from a free cover is tagged so the funnel can tell them apart
    const source = firstView.current && fromFreeCover ? { source: 'free_cover' } : {};
    firstView.current = false;
    trackCreate('picture-book', 'create_step_viewed', { step, ...source });
  }, [step, resuming, seeding]);

  // Family from the photo (Task B3): Your book shows the central-role notice for a ready-made story whose central
  // person is not in the photo. Reported once each time it appears (per template); names are never sent.
  const centralTracked = useRef<string | null>(null);
  useEffect(() => {
    const missing = step === 'your-book' ? unfilledCentralRoles(details.templateId, characters) : [];
    if (!missing.length) {
      centralTracked.current = null;
      return;
    }
    if (centralTracked.current === details.templateId) return;
    centralTracked.current = details.templateId ?? null;
    trackCreate('picture-book', 'central_role_missing', {
      template: details.templateId,
      needs: missing.map((r) => r.roleId).join(','),
    });
  }, [step, details.templateId, characters]);

  const handlePhotoChange = (next: MangaPhoto | null) => {
    // A different photo means the detected faces (and the pre-filled people) are stale.
    if (!next || next.dataUrl !== photo?.dataUrl) setCharacters([]);
    setPhoto(next);
  };

  const handlePhotoNext = () => {
    if (!photo) return;
    if (characters.length === 0) setCharacters(charactersFromFaces(photo.faces, photo.analysis));
    setStep('people');
  };

  // fromCoverSlug lets the server count the conversion on the source cover (it checks the owner cookie)
  const payload = () =>
    photo ? { ...buildPictureBookPreviewPayload({ characters, details, photo }), ...(coverSlug ? { fromCoverSlug: coverSlug } : {}) } : null;

  /** The preview key: people, photo, language and page count (style, title and idea are left out on purpose). */
  const currentKey = (): string | null => {
    const p = payload();
    return p ? pictureBookPreviewKey({ characters: p.characters, photoDataUrl: p.photoBase64, language: p.language, pageCount: p.pageCount }) : null;
  };

  /** GET a book URL for the stored-book check: status (0 = network) and body. */
  const getJson = async (url: string): Promise<FetchedJson> => {
    const headers = await bookRequestHeaders();
    const res = await fetch(withBookToken(url, accessToken), { headers, cache: 'no-store' });
    return { status: res.status, body: res.ok ? await res.json().catch(() => null) : undefined };
  };

  /**
   * Your book -> Next. With a preview already made for the same people, photo, language and page count, a
   * style/title/idea edit goes to the change route (a counted redraw), never a new book (review focus 2).
   */
  const handleDetailsNext = async () => {
    const key = currentKey();
    if (!key || nextBusy.current) return;
    nextBusy.current = true;
    // Back during "Opening your book…" cancels this Next (see handleDetailsBack)
    const run = ++nextRun.current;
    setNextError(null);
    try {
      let existing = previewBook && previewBook.key === key ? previewBook : null;
      // A stored book carries its own FLAG 1 lock; the in-page book keeps the one already held
      let storedEdited: boolean | null = null;
      if (!existing) {
        const storage = safeSessionStorage();
        const stored = readStoredPreviewBook(storage, key);
        if (stored) {
          setChecking(true);
          const id = encodeURIComponent(stored);
          const r = await resolveStoredBook(
            { getBook: () => getJson(`/api/books/${id}`), getStory: () => getJson(`/api/books/${id}/picture-book-story`) },
            detailsOf(details),
          );
          if (nextRun.current !== run) return;
          if (r.kind === 'retry') {
            // Transient: keep the stored entry and make nothing (a new preview costs money)
            setNextError(STORED_BOOK_RETRY_MESSAGE);
            return;
          }
          if (r.kind === 'reuse') {
            existing = { key, bookId: stored, details: r.details };
            storedEdited = r.edited;
          } else {
            // Gone or unusable: forget it, so it never comes back for these inputs
            clearStoredPreviewBook(storage, key);
          }
        }
      }
      if (existing && routeDetailsChange({ key: existing.key, bookId: existing.bookId }, key) === 'change-route') {
        const change = detailsChangeFields(existing.details, detailsOf(details));
        setPreviewBook(existing);
        if (storedEdited !== null) setLocked(storedEdited);
        setPendingChange(Object.keys(change).length > 0 ? change : null);
      } else {
        setPreviewBook(null);
        setPendingChange(null);
        setLocked(false);
      }
      setStep('preview');
    } finally {
      if (nextRun.current === run) {
        nextBusy.current = false;
        setChecking(false);
      }
    }
  };

  /** Your book -> Back: also cancels a Next still checking a stored book. */
  const handleDetailsBack = () => {
    nextRun.current += 1;
    nextBusy.current = false;
    setChecking(false);
    setNextError(null);
    setStep('people');
  };

  const handleBookCreated = (bookId: string) => {
    const key = currentKey();
    if (key) storePreviewBook(safeSessionStorage(), key, bookId);
    setPreviewBook({ key: key ?? '', bookId, details: detailsOf(details) });
    // A refresh resumes this book
    router.replace(`/create-book?book=${encodeURIComponent(bookId)}`, { scroll: false });
  };

  /** A change went through on the book: Your book and the baseline follow it, so Back then Next sends nothing again. */
  const handleDetailsChanged = (change: Partial<BookDetails>) => {
    setPendingChange(null);
    setPreviewBook((pb) => (pb ? { ...pb, details: { ...pb.details, ...change } } : pb));
    setDetails((d) => {
      const source = { ...d.source };
      for (const f of Object.keys(change) as Array<keyof BookDetails>) source[f] = 'parent';
      return { ...d, ...change, source };
    });
  };

  /** Start a new book: forget the preview book (and its stored key) and go back to Your book (or Photo after a resume). */
  const handleStartNewBook = () => {
    // Every stored key pointing at the abandoned book (a resumed book has no key of its own), and the resumed one
    const storage = safeSessionStorage() as unknown as EnumerableStorage | null;
    if (previewBook?.key) clearStoredPreviewBook(safeSessionStorage(), previewBook.key);
    if (previewBook?.bookId) clearStoredEntriesForBook(storage, previewBook.bookId);
    if (resumeFrom.current) clearStoredEntriesForBook(storage, resumeFrom.current);
    resumeFrom.current = null;
    setAccessToken('');
    setPreviewBook(null);
    setPendingChange(null);
    setLocked(false);
    setResumeError(null);
    setResuming(false);
    router.replace('/create-book', { scroll: false });
    setStep(photo && characters.length > 0 ? 'your-book' : 'photo');
  };

  const stepIndex = PICTURE_BOOK_FLOW_STEPS.indexOf(step);

  if (seeding) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center gap-4 px-4 text-center text-gray-600">
        <p>Opening your cover…</p>
      </div>
    );
  }

  if (resuming) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center gap-4 px-4 text-center text-gray-600">
        {resumeError ? (
          <>
            <p className="max-w-md">{resumeError}</p>
            <Button variant="outline" onClick={handleStartNewBook}>
              Start a new book
            </Button>
          </>
        ) : (
          <p>Opening your book…</p>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-pink-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="text-center space-y-1">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">Create your personalized picture book</h1>
          <p className="text-gray-600">A storybook starring your family, drawn from your photo. Preview it free.</p>
        </div>

        {/* Step indicator */}
        <ol className="flex items-center justify-center" aria-label="Steps">
          {PICTURE_BOOK_FLOW_STEPS.map((s, index) => (
            <React.Fragment key={s}>
              {index > 0 && (
                <li aria-hidden className={`h-0.5 w-5 sm:w-10 mx-1 sm:mx-2 ${stepIndex > index - 1 ? 'bg-purple-500' : 'bg-gray-300'}`} />
              )}
              <li className="flex flex-col items-center" aria-current={stepIndex === index ? 'step' : undefined}>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors ${
                    stepIndex > index
                      ? 'bg-purple-600 border-purple-600 text-white'
                      : stepIndex === index
                        ? 'bg-white border-purple-600 text-purple-700'
                        : 'bg-white border-gray-300 text-gray-400'
                  }`}
                >
                  {stepIndex > index ? <Check className="w-4 h-4" aria-hidden /> : index + 1}
                </div>
                <span className={`text-xs mt-1 ${stepIndex >= index ? 'text-purple-700 font-medium' : 'text-gray-400'}`}>
                  {STEP_LABELS[s]}
                </span>
              </li>
            </React.Fragment>
          ))}
        </ol>

        <div className="bg-white rounded-xl shadow-md p-4 sm:p-8">
          {step === 'photo' && (
            <MangaPhotoStep
              flow="picture-book"
              photo={photo}
              onPhotoChange={handlePhotoChange}
              onNext={handlePhotoNext}
              seedPhotoFile={coverPhoto ?? undefined}
              onSeedConsumed={() => setCoverPhoto(null)}
              notice={coverNotice}
              seedConsented={coverConsented}
              coverSlug={coverSlug}
            />
          )}

          {step === 'people' && (
            <MangaCharactersStep
              flow="picture-book"
              characters={characters}
              onCharactersChange={setCharacters}
              onBack={() => setStep('photo')}
              onNext={() => setStep('your-book')}
            />
          )}

          {step === 'your-book' && (
            <PictureBookDetailsStep
              people={characters}
              scene={photo?.scene ?? null}
              value={details}
              onChange={setDetails}
              onBack={handleDetailsBack}
              onNext={() => void handleDetailsNext()}
              busy={checking}
              onChangePhoto={() => setStep('photo')}
              storyNames={storyNames}
              pinnedIdea={ideaSeed}
              occasion={initialOccasion}
            />
          )}
          {step === 'your-book' && nextError && (
            <p className="mt-4 text-center text-sm text-red-600" role="alert">
              {nextError}
            </p>
          )}

          {step === 'preview' && (
            <PictureBookPreviewStep
              bookId={previewBook?.bookId ?? null}
              createPayload={payload}
              pendingChange={pendingChange}
              accessToken={accessToken}
              locked={locked}
              fallbackDetails={photo ? detailsOf(details) : previewBook?.details}
              ideaSource={ideaSourceOf(details)}
              onBookCreated={handleBookCreated}
              onDetailsChanged={handleDetailsChanged}
              onLocked={() => setLocked(true)}
              onStartNewBook={handleStartNewBook}
              // After a resume there is no photo or people to go back to: the preview's own controls change the book
              onBack={
                photo
                  ? () => {
                      setPendingChange(null);
                      setStep('your-book');
                    }
                  : undefined
              }
              onNext={() => {
                setPendingChange(null);
                setStep('story');
              }}
            />
          )}

          {step === 'story' && previewBook && (
            <PictureBookStoryStep
              bookId={previewBook.bookId}
              accessToken={accessToken}
              onEdited={setLocked}
              onStartNewBook={handleStartNewBook}
              onBack={() => setStep('preview')}
            />
          )}
        </div>
      </div>
    </div>
  );
}
